import React, { useState, useEffect } from 'react';
import { get, isArray } from 'lodash';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import { withBreakpoints } from 'react-breakpoints';
import html2canvas from 'html2canvas';
import { connect } from 'react-redux';
import { Grid, Row, Col, Alert } from 'react-bootstrap';
import { sum } from 'simple-statistics';
import CSVReader from 'react-csv-reader';
import { LoadingTile } from 'components/molecules/ErrorTile';
import ButtonIconSecondary from 'components/molecules/buttons/ButtonIconSecondary';
import ButtonIconPrimary from 'components/molecules/buttons/ButtonIconPrimary';
import ContentTile from 'components/atoms/layout/ContentTile';
import Overlay from 'components/atoms/layout/Overlay';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import ATVLift from 'components/molecules/charts/labs/ATVLift';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import { fetchEntityATVLabData, refreshEntityATVLabData, updateEntityATVLabCSV, fetchMeta } from 'services/entities/duck';
import CrossChannelDataTable from 'components/molecules/tables/labs/ATVDataTable';
import helpers from 'helpers';
import { getPointWidth } from 'helpers/labs/charts';
import TTDLogo from 'assets/ttd-logo.png';
import ATVGlossary from 'constants/glossaries/ATVGlossary';
import TrackedComponentEnhancer from 'hoc/TrackedComponent';

const TrackedShareButton = TrackedComponentEnhancer({
    trackedName: 'ATV-Lift-Share-Button-Click',
    targetComponent: ButtonIconPrimary,
    sendToMixPanel: true
});

const UpdateWrapper = styled.div`
    background-color: #F2FAFF;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-radius: 5px;
    width: 100%;
    margin-bottom: 32px;

    ${breakpoint('xs', 'sm')`
       flex-direction: column;
       height: 85px;
    `}
`;

const csvReaderParseOptions = {
    header: true,
};

const CopyPadding = styled.div`
    padding: 10px 10px 10px 15px;
`;

const createPercentage = (portion, total) => {
    const percentage = parseFloat(
        (helpers.safeDivide(portion, total) * 100).toFixed(4)
    );
    return percentage;
};

const LayerThreeLabsATVLiftContainer = ({ dispatch, meta, currentBreakpoint, crossChannelData = {} }) => {
    const { workOrders = [], id } = meta;
    let refreshData;
    let initialLoad;
    let rows;
    let lastExecution;
    let currentBreakpointWidth = getPointWidth(currentBreakpoint);

    if (isArray(crossChannelData)) {
        // the isArray check is used to identify CSV uploaded data.
        // It was saved in a different shape than direct data layer query data
        refreshData = false;
        initialLoad = false;
        rows = crossChannelData;
        lastExecution = null;
    } else {
        refreshData = get(crossChannelData, 'refresh', false);
        initialLoad = crossChannelData.hasData === undefined && !refreshData;
        rows = get(crossChannelData, 'rows', []);
        lastExecution = get(crossChannelData, 'lastExecution', null);
    }
    const [loading, setLoading] = useState(initialLoad);
    const [mountModal, setMountModal] = useState(false);
    const [CSVData, setCSVData] = useState([]);
    const hasData = CSVData.length || rows.length || get(crossChannelData, 'hasData', false);
    const resetATVData = () => {
        dispatch(refreshEntityATVLabData());
    };

    const exportLiftChart = () => {
        setMountModal(true);
    };

    useEffect(() => {
        if (mountModal) {
            html2canvas(document.querySelector('#AtvLiftChartForSharing'), {
                scrollY: -window.scrollY, // ensure the full image is captured
                scale: 2
            }).then((canvas) => {
                const imgDataUrl = canvas.toDataURL('image/png');
                document.querySelector('#ShareOverlay img').src = imgDataUrl;
            });
        }
    }, [mountModal]);
    useEffect(() => {
        currentBreakpointWidth = getPointWidth(currentBreakpoint);
        setLoading(true);
        if (initialLoad) {
            dispatch(
                fetchEntityATVLabData({
                    entityId: parseInt(id, 10),
                    transactionId: `entity-${id}-atv-lab-data`,
                    refresh: false
                }));
        } else if (refreshData) {
            dispatch(
                fetchEntityATVLabData({
                    entityId: parseInt(id, 10),
                    transactionId: `entity-${id}-atv-lab-data-refresh`,
                    refresh: true
                }));
        } else {
            setLoading(false);
        }
    }, [initialLoad, refreshData]);

    useEffect(() => {
        dispatch(
            fetchMeta({
                transactionId: `entity-${id}-summary`,
                entityId: id,
            })
        );

        if (CSVData.length) {
            dispatch(
                updateEntityATVLabCSV({
                    data: { data: CSVData },
                    entityId: parseInt(id, 10),
                    transactionId: `entity-${id}-summary`,
                    name: meta.name,
                })
            );
        }
    }, [CSVData]);

    const opps = workOrders.filter(r => r.salesforceId);
    const oppIds = opps.map(v => `'${v.salesforceId}'`).join(',') || "''";

    const UploadSection = <ContentTile padding="20px">
        <p style={{ fontSize: '18px', fontWeight: 'bold' }}>
            {`${(CSVData.length || crossChannelData.length)
                ? 'Having trouble refreshing the data?'
                : 'Having trouble loading the data?'
            }`}
        </p>
        <ol>
            <li>
                Open{' '}
                <a target="_blank"
                    href={`https://gw-data.goodwaygroup.com/queries/741?p_opportunity_ids%20"(%27abc%27,%27abc%27)"=(${oppIds})`}>
                    this query in Redash
                </a>
            </li>
            <li>Export the query results as a CSV</li>
            <li>Upload the CSV below</li>
        </ol>
        <br />
        <CSVReader
            parserOptions={csvReaderParseOptions}
            onFileLoaded={data => setCSVData(data)}
        />
    </ContentTile>;

    const dataForRender = (CSVData.length ? CSVData : rows).filter(
        row => row.channel_count
    );

    const nonATVData = dataForRender.filter(
        row => row.channel && !row.channel.includes('ctv')
    );
    const hasATVData = !!dataForRender.filter(row => row.channel.includes('ctv')).length;
    const combinatorialATVData = dataForRender.filter(
        row =>
            row.channel &&
            row.channel.includes('ctv') &&
            row.channel_type === 'multi-channel'
    );
    const nonATVUsers = sum(nonATVData.map(r => +r.households));
    const nonATVConverters = sum(nonATVData.map(r => +r.converters));
    const nonATVConversionRate = createPercentage(nonATVConverters, nonATVUsers);
    const combinatorialATVUsers = sum(combinatorialATVData.map(r => +r.households));
    const combinatorialATVConverters = sum(combinatorialATVData.map(r => +r.converters));
    const combinatorialATVConversionRate = createPercentage(combinatorialATVConverters, combinatorialATVUsers);
    const ATVLiftPercentage = Math.ceil(
        createPercentage(
            combinatorialATVConversionRate - nonATVConversionRate,
            nonATVConversionRate
        )
    );
    const hasATVLift = ATVLiftPercentage > 0;

    // Video and ATV
    const nonATVOrVideoData = dataForRender.filter(row => row.channel && (!row.channel.includes('ctv') && !row.channel.includes('video')));
    const hasVideoData = !!dataForRender.filter(row => row.channel && row.channel.includes('video')).length;
    const combinatorialATVAndVideoData = dataForRender.filter(
        row => (row.channel && row.channel.includes('ctv') && row.channel_type === 'multi-channel') ||
            (row.channel && row.channel.includes('video') && row.channel_type === 'multi-channel')
    );
    const nonATVOrVideoUsers = sum(nonATVOrVideoData.map(r => +r.households));
    const nonATVorVideoConverters = sum(nonATVOrVideoData.map(r => +r.converters));
    const nonATVOrVideoConversionRate = createPercentage(nonATVorVideoConverters, nonATVOrVideoUsers);
    const combinatorialATVAndVideoUsers = sum(combinatorialATVAndVideoData.map(r => +r.households));
    const combinatorialATVAndVideoConverters = sum(combinatorialATVAndVideoData.map(r => +r.converters));
    const combinatorialATVAndVideoConversionRate =
        createPercentage(combinatorialATVAndVideoConverters, combinatorialATVAndVideoUsers);
    const ATVAndVideoLiftPercentage = Math.ceil(
        createPercentage(
            combinatorialATVAndVideoConversionRate - nonATVOrVideoConversionRate,
            nonATVOrVideoConversionRate
        )
    );
    const hasATVAndVideoLift = ATVAndVideoLiftPercentage > 0;

    const UpdateBlock =
        <UpdateWrapper>
            <div>This data was last updated - <b>{`${lastExecution || 'NA'}`}</b></div>
            <ButtonIconSecondary
                icon="refresh"
                iconColor="#007DC6"
                onClick={() => resetATVData()}
            >
                {'Refresh'}
            </ButtonIconSecondary>
        </UpdateWrapper>;

    return (
        (CSVData.length === 0 && crossChannelData.length === 0) ?
            <Grid fluid>
                <ChannelTitle>
                    Advanced TV Cross-Channel Visualizer
                </ChannelTitle>
                <Row>
                    <Col sm={12}>
                        {UploadSection}
                    </Col>
                </Row>
            </Grid>
            :
            <Grid fluid>
                <Row>
                    <Col sm={12} lg={8}>
                        <ChannelTitle>
                            Advanced TV Cross-Channel Visualizer
                        </ChannelTitle>
                    </Col>
                </Row>
                {loading &&
                    <Row>
                        <Col sm={12} lg={8}>
                            <LoadingTile>
                                We are currently gathering the data needed.
                                This could take a few seconds to a couple of minutes.
                                How about a fresh cup of coffee?
                            </LoadingTile>
                        </Col>
                    </Row>
                }
                {hasData &&
                    <Row>
                        <Col sm={12} lg={8}>

                            <React.Fragment>
                                <div
                                    id="AtvLiftChartForSharing"
                                    style={{
                                        padding: mountModal ? '20px 20px 0px 10px' : 0
                                        // bottom padding is zero because the hidden video toggle
                                        // using 'data-html2canvas-ignore' creates an artifact of padding already
                                    }}
                                >
                                    <ATVLift
                                        title={'Advanced TV: Lift in Conversion Rate'}
                                        combinatorialATVConversionRate={combinatorialATVConversionRate}
                                        combinatorialATVAndVideoConversionRate={combinatorialATVAndVideoConversionRate}
                                        nonATVConversionRate={nonATVConversionRate}
                                        nonATVOrVideoConversionRate={nonATVOrVideoConversionRate}
                                        ATVLiftPercentage={ATVLiftPercentage}
                                        ATVAndVideoLiftPercentage={ATVAndVideoLiftPercentage}
                                        hasATVLift={hasATVLift}
                                        hasATVAndVideoLift={hasATVAndVideoLift}
                                        hasVideoData={hasVideoData}
                                        hasATVData={hasATVData}
                                        data={dataForRender}
                                        animation={!mountModal}
                                    />
                                </div>
                                <CrossChannelDataTable data={dataForRender} />
                                {Boolean(lastExecution || dataForRender.length) &&
                                    UpdateBlock
                                }
                            </React.Fragment>
                        </Col>
                        <Col sm={12} lg={4}>
                            <ContentTile>
                                <ContentHeader hideCSVExport={true}>Glossary</ContentHeader>
                                <CopyPadding>
                                    <ul style={{ listStyleType: 'none', padding: '0px' }}>
                                        {ATVGlossary.map((key, i) =>
                                            <li key={i} style={{ marginBottom: '10px' }}><b>{key.displayName}</b> - {key.definition}</li>
                                        )}
                                    </ul>
                                </CopyPadding>
                            </ContentTile>
                        </Col>
                        {
                            (hasATVLift || hasATVAndVideoLift) && (currentBreakpointWidth > 200) && // hide share ability on mobile and when no lift
                            <Col sm={12} lg={4}>
                                <div
                                    style={{ textAlign: 'left', padding: 5 }} // left margin matches up with ContentTile defaults
                                >
                                    <TrackedShareButton
                                        icon="share"
                                        iconColor="#fff"
                                        onClick={exportLiftChart}
                                        payload={{
                                            ATVLiftPercentage,
                                            ATVAndVideoLiftPercentage
                                        }}
                                    >
                                        {'Share Results'}
                                    </TrackedShareButton>
                                </div>
                            </Col>
                        }
                    </Row>
                }
                {
                    (!hasData && !loading) &&
                    <Row>
                        <Col sm={12} lg={8}>
                            <Alert bsStyle="warning">
                                <strong>No Cross Channel Data Found</strong><br />
                       We could not detect any cross channel data. This lab looks at the last 100 days of data.
                                <ul>
                                    <li>If you just started running your campaign, please check back in a week.</li>
                                    <li>If this is an older campaign,
                                you might not have any data to query for the last 100 days.</li>
                                </ul>
                            </Alert>
                            {lastExecution &&
                                UpdateBlock
                            }
                        </Col>
                    </Row>
                }
                <Row>
                    <Col sm={12} lg={8}>
                        <ContentTile>
                            <ContentHeader hideCSVExport={true}>About this Lab</ContentHeader>
                            <CopyPadding>The goal of this lab is to show the amount of
                            Conversion Rate Lift an entity is benefiting from by incorporating
                            ATV ads into its media strategies. Since conversions typically don't
                            happen though TVs, it is hard to measure the effectiveness of ATV
                            with traditional advertising KPIs. We can, however, show how ATV
                            effectively moves viewers down the funnel towards a conversion.
                            <br />
                            <br />
                            {hasVideoData &&
                                    <React.Fragment>
                                        <span>If your campaign classifies ATV as video then be sure to enable the
                                "INCLUDE VIDEO AS PART OF ATV" toggle.</span>
                                        <br />
                                        <br />
                                    </React.Fragment>
                            }

                            <em>Based on conversion data provided by:</em>
                            <br />
                            <img src={TTDLogo} style={{ width: '23px', height: '23px', margin: 0 }} /><b>The Trade Desk</b>
                            </CopyPadding>
                        </ContentTile>
                    </Col>

                    <Col sm={12} lg={8}>
                        {UploadSection}
                    </Col>

                </Row>
                <Overlay
                    id="ShareOverlay"
                    mountModal={mountModal}
                    style={{ paddingTop: 200 }}
                    onClick={() => setMountModal(false)}
                >
                    <div
                        onClick={e => e.stopPropagation()} // prevent closure of modal when clicked
                        style={{
                            width: window.innerWidth * 0.6,
                            margin: ' auto'
                        }}>
                        <h2 style={{ color: '#fff' }}>Right click the image below to copy or save:</h2>
                        <img style={{ maxWidth: '100%', height: 'auto' }} />
                        <div style={{ display: 'block', paddingTop: 5 }}>
                            <ButtonIconSecondary
                                icon="close-circle"
                                iconColor="#007DC6"
                                onClick={() => {
                                    setMountModal(false);
                                }}
                            >
                                {'Close'}
                            </ButtonIconSecondary>
                        </div>
                    </div>
                </Overlay>
            </Grid>


    );
};

const mapStateToProps = state => ({
    theme: state.theme,
    meta: get(state, 'entities.meta', {}),
    crossChannelData: get(state, 'entities.meta.meta.labs.crossChannelData'),
});
export default connect(mapStateToProps)(withBreakpoints(LayerThreeLabsATVLiftContainer));




// WEBPACK FOOTER //
// ./src/containers/labs/LabsATVLiftContainer.js